/**
 * Argument builder class and type constants for SPDS component metadata.
 */

import {
  ACTION,
  ARRAY,
  BOOLEAN,
  COMPONENT,
  FUNCTION,
  NUMBER,
  OBJECT,
  STRING,
} from '../../component-validations/constants';

/**
 * Type constant mapping for use in arg definitions.
 * Re-exported for convenience when defining args outside of the Arg class.
 */
export const ArgTypes = {
  ACTION,
  ARRAY,
  BOOLEAN,
  COMPONENT,
  FUNCTION,
  NUMBER,
  OBJECT,
  STRING,
};

/**
 * Argument builder class for defining component argument metadata.
 * Provides a fluent API for creating argument configurations.
 */
export class Arg {
  #config = {};

  constructor(type, config = {}) {
    this.#config = { type, ...config };
  }

  // ============ Private Helpers ============

  /**
   * Normalizes config by mapping 'default' to 'defaultValue' and ensuring description exists.
   * @param {Object} config - Raw config from factory method
   * @param {*} [typeDefault] - Type-specific default value (undefined if no default)
   * @returns {Object} - Normalized config
   */
  static #normalizeConfig(config, typeDefault) {
    const { default: defaultVal, ...rest } = config;

    return {
      description: config.description ?? '',
      defaultValue: defaultVal ?? typeDefault,
      ...rest,
    };
  }

  /**
   * Creates an Arg instance with normalized config.
   * @param {string} type - The argument type constant
   * @param {Object} config - User-provided config
   * @param {*} [typeDefault] - Type-specific default value
   * @param {Object} [extras] - Additional properties to merge
   * @returns {Arg}
   */
  static #create(type, config, typeDefault, extras = {}) {
    return new Arg(type, {
      ...Arg.#normalizeConfig(config, typeDefault),
      ...extras,
    });
  }

  // ============ Static Factory Methods ============

  /**
   * Creates a boolean argument.
   * @param {Object} config - Argument configuration
   * @param {boolean} [config.default=false] - Default value
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static boolean(config = {}) {
    return Arg.#create(BOOLEAN, config, false);
  }

  /**
   * Creates a string argument.
   * @param {Object} config - Argument configuration
   * @param {string} [config.default=''] - Default value
   * @param {string} [config.description] - Argument description
   * @param {Array<string>} [config.options] - Valid options
   * @returns {Arg}
   */
  static string(config = {}) {
    return Arg.#create(STRING, config, '', { options: config.options });
  }

  /**
   * Creates a number argument.
   * @param {Object} config - Argument configuration
   * @param {number} [config.default=0] - Default value
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static number(config = {}) {
    return Arg.#create(NUMBER, config, 0);
  }

  /**
   * Creates an action (function) argument.
   * @param {Object} config - Argument configuration
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static action(config = {}) {
    return Arg.#create(ACTION, config);
  }

  /**
   * Creates a function argument.
   * @param {Object} config - Argument configuration
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static function(config = {}) {
    return Arg.#create(FUNCTION, config);
  }

  /**
   * Creates an array argument.
   * @param {Object} config - Argument configuration
   * @param {Array} [config.default=[]] - Default value
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static array(config = {}) {
    return Arg.#create(ARRAY, config, []);
  }

  /**
   * Creates an object argument.
   * @param {Object} config - Argument configuration
   * @param {Object} [config.default={}] - Default value
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static object(config = {}) {
    return Arg.#create(OBJECT, config, {});
  }

  /**
   * Creates a component argument.
   * @param {Object} config - Argument configuration
   * @param {string} [config.description] - Argument description
   * @returns {Arg}
   */
  static component(config = {}) {
    return Arg.#create(COMPONENT, config);
  }

  /**
   * Creates a string argument with predefined options (enum-like).
   * @param {Array<string>} options - Valid options
   * @param {Object} config - Additional configuration
   * @returns {Arg}
   */
  static oneOf(options, config = {}) {
    return Arg.#create(STRING, config, config.default ?? options[0], { options });
  }

  // ============ Chainable Modifiers ============

  /**
   * Sets the default value.
   * @param {*} value - Default value
   * @returns {Arg}
   */
  default(value) {
    this.#config.defaultValue = value;
    return this;
  }

  /**
   * Sets the initial value for documentation.
   * @param {*} value - Initial value
   * @returns {Arg}
   */
  initial(value) {
    this.#config.initialValue = value;
    return this;
  }

  /**
   * Sets the description.
   * @param {string} text - Description text
   * @returns {Arg}
   */
  description(text) {
    this.#config.description = text;
    return this;
  }

  /**
   * Marks the argument as required.
   * @returns {Arg}
   */
  required() {
    this.#config.required = true;
    return this;
  }

  /**
   * Marks the argument as optional.
   * @returns {Arg}
   */
  optional() {
    this.#config.required = false;
    return this;
  }

  /**
   * Marks the argument as private (not in public API).
   * @returns {Arg}
   */
  private() {
    this.#config.private = true;
    return this;
  }

  /**
   * Specifies arguments that cannot be used with this one.
   * @param {Array<Object>} args - Arguments to forbid
   * @returns {Arg}
   */
  forbids(args) {
    this.#config.forbids = Array.isArray(args) ? args : [args];
    return this;
  }

  /**
   * Specifies arguments that must be present with this one.
   * @param {Array<Object>} args - Required arguments
   * @returns {Arg}
   */
  requires(args) {
    this.#config.requires = Array.isArray(args) ? args : [args];
    return this;
  }

  /**
   * Sets the CSS custom property name for this argument.
   * @param {string} name - CSS property name
   * @returns {Arg}
   */
  cssProperty(name) {
    this.#config.cssProperty = name;
    return this;
  }

  /**
   * Sets a conditional default that depends on another argument's value.
   * When the dependency arg matches a key in the value map, the corresponding
   * value is used as the default. The dependency arg is resolved from raw args
   * with its own static default as fallback.
   * @param {string} argName - The argument this default depends on
   * @param {Object} valueMap - Map of dependency arg values (as strings) to default values
   * @returns {Arg}
   *
   * @example
   * Arg.string({ description: 'CSS align-items' })
   *   .defaultWhen('advanced', { true: 'initial', false: 'center' })
   */
  defaultWhen(argName, valueMap) {
    this.#config.conditionalDefault = { arg: argName, values: valueMap };
    return this;
  }

  /**
   * Sets valid options for string arguments.
   * @param {Array<string>} values - Valid options
   * @returns {Arg}
   */
  options(values) {
    this.#config.options = values;
    return this;
  }

  /**
   * Defines nested property validations for object arguments.
   * Properties are flattened to dot-notation keys during metadata processing.
   * @param {Object} propsMap - Map of property names to Arg instances
   * @returns {Arg}
   */
  properties(propsMap) {
    this.#config.properties = propsMap;
    return this;
  }

  /**
   * Converts the Arg instance to a plain configuration object.
   * @returns {Object}
   */
  toConfig() {
    return { ...this.#config };
  }
}

export default Arg;
